/**
 * The Envelope — what there is, and what's asking for it.
 * One screen to set up the whole allocation; the method is a guide moment.
 */

import { useEffect, useMemo, useState } from 'react';
import { uniqueLines } from '@/lib/uniqueLines';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { useSessionStore } from '@/stores/prioritization/sessionStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import { getAppDefinition } from '../registry';
import { AppShell, BallLine, ScreenHeading } from '../components/AppShell';
import {
  AppCard,
  EmptyState,
  Eyebrow,
  PrimaryButton,
  StatusChip,
} from '../components/primitives';
import { GuideMomentCard } from '../components/flow';
import { useToast } from '@/components/ui/Toast';
import { ALLOCATION_METHODS, allocationMethodName, createAllocation } from './allocateActions';
import { SESSION_CREATE_BALL, SESSION_CREATE_LINE, useCreateRights } from '@/lib/authorization/createRights';

export default function EnvelopeScreen() {
  const app = getAppDefinition('allocate')!;
  const navigate = useNavigate();
  const userId = useUserStore((s) => s.userId);
  const mayCreateSession = useCreateRights().session;
  const { showToast } = useToast();
  const sessions = useSessionStore((s) => s.sessions);
  const fetchSessions = useSessionStore((s) => s.fetchFromSupabase);

  const [question, setQuestion] = useState('');
  const [total, setTotal] = useState('');
  const [unit, setUnit] = useState('$');
  const [asksText, setAsksText] = useState('');
  const [toolId, setToolId] = useState<string | null>(null);
  const [choosing, setChoosing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void fetchSessions();
  }, [fetchSessions]);

  const asks = useMemo(
    () => uniqueLines(asksText.split('\n')),
    [asksText],
  );

  // Buy-a-Feature shines with a room and a real budget; Cost-of-Delay is the
  // recommendation when there are many asks and the answer must be defensible.
  const recommended = asks.length > 8 ? 'cost-of-delay' : 'buy-a-feature';
  const effectiveTool = toolId ?? recommended;
  const recommendedMeta = ALLOCATION_METHODS.find((m) => m.id === recommended)!;

  const mine = useMemo(
    () =>
      sessions
        .filter(
          (s) =>
            s.facilitatorId === userId &&
            ALLOCATION_METHODS.some((m) => m.id === s.selectedToolId),
        )
        .sort(
          (a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime(),
        ),
    [sessions, userId],
  );

  const canCreate = question.trim().length > 0 && asks.length >= 2 && !busy;

  const create = async () => {
    if (!userId) return;
    setBusy(true);
    setError(null);
    const parsedTotal = Number(total.replace(/[^0-9.]/g, ''));
    const result = await createAllocation({
      name: question.trim(),
      asks,
      toolId: effectiveTool,
      envelopeTotal: Number.isFinite(parsedTotal) && parsedTotal > 0 ? parsedTotal : undefined,
      envelopeUnit: unit.trim() || undefined,
      facilitatorId: userId,
      toast: showToast,
    });
    setBusy(false);
    if (!result.ok) {
      setError(result.error ?? 'That allocation could not be created. Try again.');
      return;
    }
    navigate(`/apps/allocate/${result.sessionId}`);
  };

  return (
    <AppShell app={app}>
      <ScreenHeading
        title="Split what you have"
        subtitle="Name the envelope, list what's asking for it, and let people spend it."
      />
      <BallLine ballWith={mayCreateSession ? 'you — define the envelope' : SESSION_CREATE_BALL} />

      <div className="space-y-4">
        {/* Creating inserts into prioritization_sessions, whose insert rule
            (migration 754) takes SESSION_RUN: the form is drawn only for its
            holders, the line for everyone else (build contract §2.8). */}
        {mayCreateSession ? (
          <>
            <AppCard>
              <Eyebrow>1 · What are you splitting?</Eyebrow>
              <input
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="e.g. How do we split next quarter's build budget?"
                className="mt-2 w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 font-body text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-600 focus:outline-none dark:border-[var(--rule)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-pri)]"
              />
              <div className="mt-3 flex gap-2">
                <input
                  value={unit}
                  onChange={(e) => setUnit(e.target.value)}
                  placeholder="$"
                  aria-label="Unit"
                  className="w-20 rounded-md border border-gray-300 bg-white px-3 py-2.5 font-body text-sm dark:border-[var(--rule)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-pri)]"
                />
                <input
                  value={total}
                  onChange={(e) => setTotal(e.target.value)}
                  placeholder="Total to split (optional)"
                  inputMode="decimal"
                  className="flex-1 rounded-md border border-gray-300 bg-white px-3 py-2.5 font-body text-sm dark:border-[var(--rule)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-pri)]"
                />
              </div>
              <p className="mt-1 font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
                No total? You&apos;ll still get each ask&apos;s share of the room&apos;s budget.
              </p>
            </AppCard>

            <AppCard>
              <Eyebrow>2 · What&apos;s asking for it?</Eyebrow>
              <textarea
                value={asksText}
                onChange={(e) => setAsksText(e.target.value)}
                rows={6}
                placeholder={'One ask per line, e.g.\nSelf-serve onboarding\nPartner API\nEU data residency'}
                className="mt-2 w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 font-body text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary-600 focus:outline-none dark:border-[var(--rule)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-pri)]"
              />
              <p className="mt-1 font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
                {asks.length} ask{asks.length === 1 ? '' : 's'} — you need at least 2.
              </p>
            </AppCard>

            <div>
              <div className="mb-2">
                <Eyebrow>3 · How should people decide?</Eyebrow>
              </div>
              {!choosing ? (
                <GuideMomentCard
                  question="How should the room split it?"
                  recommendation={recommendedMeta.name}
                  why={
                    recommended === 'buy-a-feature'
                      ? 'With this many asks, spending a budget makes the trade-offs real and keeps everyone engaged.'
                      : 'With a long list, ranking by what waiting costs gives you the most defensible split.'
                  }
                  acceptLabel={canCreate ? 'Use it and create' : 'Use the recommendation'}
                  onAccept={() => {
                    setToolId(recommended);
                    if (canCreate) void create();
                  }}
                  onAdjust={() => setChoosing(true)}
                  adjustLabel="See both"
                />
              ) : (
                <AppCard>
                  <div className="space-y-2">
                    {ALLOCATION_METHODS.map((m) => (
                      <label
                        key={m.id}
                        className="flex cursor-pointer items-start gap-3 rounded-md px-2 py-2 hover:bg-gray-50 dark:hover:bg-[var(--hover-tint)]"
                      >
                        <input
                          type="radio"
                          name="method"
                          checked={effectiveTool === m.id}
                          onChange={() => setToolId(m.id)}
                          className="mt-1 h-4 w-4"
                        />
                        <span className="min-w-0">
                          <span className="block font-body text-sm font-medium text-gray-900 dark:text-[var(--text-pri)]">
                            {m.name}
                            {m.id === recommended ? ' · recommended' : ''}
                          </span>
                          <span className="block font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
                            {m.blurb}
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                </AppCard>
              )}
            </div>

            {error ? (
              <p className="font-body text-sm text-luminous-coral-700 dark:text-luminous-coral-300">
                {error}
              </p>
            ) : null}

            <div className="flex justify-end">
              <PrimaryButton onClick={() => void create()} disabled={!canCreate}>
                <Plus className="h-4 w-4" /> {busy ? 'Creating…' : 'Create the allocation'}
              </PrimaryButton>
            </div>
          </>
        ) : (
          <p className="font-body text-sm text-gray-500 dark:text-[var(--text-ter)]" data-testid="session-create-refused">
            {SESSION_CREATE_LINE}
          </p>
        )}

        {mine.length > 0 ? (
          <AppCard className="!p-0">
            <div className="px-5 pt-5">
              <Eyebrow>Your allocations</Eyebrow>
            </div>
            <ul className="mt-2">
              {mine.slice(0, 8).map((s) => (
                <li key={s.id} className="border-b border-gray-100 last:border-0 dark:border-[var(--rule)]">
                  <button
                    type="button"
                    onClick={() => navigate(`/apps/allocate/${s.id}`)}
                    className="flex w-full items-center gap-3 px-5 py-3 text-left hover:bg-gray-50 dark:hover:bg-[var(--hover-tint)]"
                  >
                    <span className="min-w-0 flex-1 truncate font-body text-sm text-gray-900 dark:text-[var(--text-pri)]">
                      {s.name}
                    </span>
                    <span className="shrink-0 font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
                      {allocationMethodName(s.selectedToolId)}
                    </span>
                    <StatusChip
                      label={s.state === 'PUBLISHED' ? 'Published' : s.state === 'RUN_COMPLETE' ? 'Split ready' : 'Open'}
                      tone={s.state === 'DRAFT' ? 'neutral' : 'green'}
                    />
                  </button>
                </li>
              ))}
            </ul>
          </AppCard>
        ) : (
          <EmptyState
            title="No allocations yet"
            hint="Define an envelope above and you'll have something to split in under a minute."
          />
        )}
      </div>
    </AppShell>
  );
}
